import { useState, useEffect } from "react";
import axios from "axios";
import RegionalLayout from "./RegionalLayout";
import { useTheme } from "@/contexts/ThemeContext";
import { Users, Building2, Award, Clock, Briefcase, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const mono = { fontFamily: "var(--font-mono)" };
const crd = {
  background: "var(--surface-card)",
  border: "var(--border-width, 1px) solid var(--border-default)",
  borderRadius: "1rem",
  boxShadow: "var(--shadow-card)",
};
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const labelS = { ...tm, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };
const bdr = { borderColor: "var(--border-default)" };

const COLORS = ["#00AEEF", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#6366F1", "#14B8A6", "#F97316", "#84CC16"];

function getAuthHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem("regionalToken") || localStorage.getItem("adminToken")}` } };
}

/* ── Empty Data Overlay ── */
function EmptyOverlay({ isEmpty, children, isDark }) {
  if (!isEmpty) return children;
  return (
    <div className="relative">
      <div className="opacity-[0.06] pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl"
        style={{
          background: isDark
            ? "linear-gradient(135deg, rgba(15,15,15,0.92) 0%, rgba(30,30,40,0.95) 50%, rgba(15,15,15,0.92) 100%)"
            : "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(245,247,250,0.96) 50%, rgba(255,255,255,0.92) 100%)",
          backdropFilter: "blur(2px)",
        }}
        data-testid="empty-data-overlay"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
          style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#555" : "#aaa"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="15" x2="15" y2="15" />
          </svg>
        </div>
        <p className="text-sm font-semibold" style={tp}>Data not provided</p>
        <p className="text-xs mt-1 max-w-[220px] text-center" style={tm}>
          Profile information has not been submitted for this region
        </p>
      </div>
    </div>
  );
}

/* ── Department Breakdown (Horizontal Bar) ── */
function DepartmentChart({ data, isDark }) {
  const chartData = data.slice(0, 10).map(d => ({ ...d, name: d.name.length > 30 ? d.name.slice(0, 28) + "..." : d.name }));
  return (
    <div style={crd} className="p-6" data-testid="dept-chart">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-4 h-4" style={{ color: "#00AEEF" }} />
        <p className="text-sm font-semibold" style={tp}>Department Distribution</p>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical" barSize={16} margin={{ left: 10, right: 20 }}>
          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }} />
          <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11, fill: isDark ? "#d1d5db" : "#374151" }} />
          <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: 12 }} />
          <Bar dataKey="count" fill="#00AEEF" radius={[0, 6, 6, 0]} name="Employees" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Tenure Donut ── */
function TenureDonut({ data, isDark }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div style={crd} className="p-6" data-testid="tenure-chart">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4" style={{ color: "#8B5CF6" }} />
        <p className="text-sm font-semibold" style={tp}>Tenure Distribution</p>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-40 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="band" cx="50%" cy="50%"
                innerRadius={45} outerRadius={70} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2 flex-1">
          {data.map((d, i) => (
            <div key={d.band} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="text-xs" style={ts}>{d.band}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{ ...mono, ...tp }}>{d.count}</span>
                <span className="text-[10px]" style={tm}>({total > 0 ? Math.round(d.count / total * 100) : 0}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Grade Breakdown ── */
function GradeBreakdown({ data, isDark }) {
  // Simplify grades by extracting the core level
  const simplified = {};
  data.forEach(g => {
    let key = g.grade;
    const match = g.grade.match(/^(\d+)/);
    if (match) {
      const num = match[1];
      const labels = { "2": "Grade 2 — Support", "3": "Grade 3 — Professional", "4": "Grade 4 — Manager", "5": "Grade 5 — Senior Executive", "6": "Grade 6 — VP / Leadership" };
      key = labels[num] || `Grade ${num}`;
    }
    simplified[key] = (simplified[key] || 0) + g.count;
  });
  const chartData = Object.entries(simplified)
    .map(([grade, count]) => ({ grade, count }))
    .sort((a, b) => b.count - a.count);
  const total = chartData.reduce((s, d) => s + d.count, 0);

  return (
    <div style={crd} className="p-6" data-testid="grade-chart">
      <div className="flex items-center gap-2 mb-4">
        <Award className="w-4 h-4" style={{ color: "#F59E0B" }} />
        <p className="text-sm font-semibold" style={tp}>Grade Level Distribution</p>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-40 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="count" nameKey="grade" cx="50%" cy="50%"
                innerRadius={45} outerRadius={70} paddingAngle={2}>
                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2 flex-1">
          {chartData.map((d, i) => (
            <div key={d.grade} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="text-xs truncate max-w-[160px]" style={ts}>{d.grade}</span>
              </div>
              <span className="text-xs font-semibold" style={{ ...mono, ...tp }}>{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Manager Team Size ── */
function ManagerTeamSize({ data, isDark }) {
  const chartData = data.slice(0, 10);
  return (
    <div style={crd} className="p-6" data-testid="manager-chart">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4" style={{ color: "#10B981" }} />
        <p className="text-sm font-semibold" style={tp}>Team Size per Manager</p>
        <span className="text-[10px] ml-auto" style={tm}>Top {chartData.length}</span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical" barSize={16} margin={{ left: 10, right: 20 }}>
          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? "#9ca3af" : "#6b7280" }} />
          <YAxis type="category" dataKey="manager" width={150}
            tick={{ fontSize: 11, fill: isDark ? "#d1d5db" : "#374151" }}
            tickFormatter={v => v.length > 20 ? v.slice(0, 18) + "..." : v} />
          <Tooltip contentStyle={{ background: isDark ? "#1f2937" : "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: 12 }} />
          <Bar dataKey="team_size" fill="#10B981" radius={[0, 6, 6, 0]} name="Direct Reports" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Operating Entities ── */
function EntitiesTable({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div style={crd} className="p-6" data-testid="entities-table">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase className="w-4 h-4" style={{ color: "#6366F1" }} />
        <p className="text-sm font-semibold" style={tp}>Operating Entities</p>
      </div>
      <div className="space-y-2">
        {data.map((d, i) => {
          const pct = total > 0 ? Math.round(d.count / total * 100) : 0;
          return (
            <div key={i}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs truncate max-w-[200px]" style={ts}>{d.entity}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold" style={{ ...mono, ...tp }}>{d.count}</span>
                  <span className="text-[10px]" style={tm}>({pct}%)</span>
                </div>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-muted)" }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── MAIN PAGE ── */
export default function TeamComposition() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/regional/team-composition`, getAuthHeaders());
      setData(res.data);
    } catch (e) {
      console.error("Team composition fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading || !data) {
    return (
      <RegionalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-sm" style={ts}>Loading team composition...</div>
        </div>
      </RegionalLayout>
    );
  }

  return (
    <RegionalLayout>
      <div className="max-w-[1440px] mx-auto space-y-5" data-testid="team-composition">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={tp}>Team Composition</h1>
            <p className="text-sm mt-0.5" style={ts}>
              Organizational structure and workforce breakdown &middot; {data.total} professionals
            </p>
          </div>
          <button onClick={fetchData}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
            style={{ background: "var(--surface-muted)", color: "var(--text-secondary)" }}
            data-testid="team-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        {/* Row 1: Department Chart (wide) */}
        <EmptyOverlay isEmpty={data.departments.length === 0} isDark={isDark}>
          <DepartmentChart data={data.departments.length > 0 ? data.departments : [{name: "—", count: 0},{name: "—", count: 0},{name: "—", count: 0}]} isDark={isDark} />
        </EmptyOverlay>

        {/* Row 2: Tenure + Grade side by side */}
        <div className="grid grid-cols-2 gap-4">
          <EmptyOverlay isEmpty={data.tenure_distribution.every(d => d.count === 0)} isDark={isDark}>
            <TenureDonut data={data.tenure_distribution} isDark={isDark} />
          </EmptyOverlay>
          <EmptyOverlay isEmpty={data.grades.length === 0} isDark={isDark}>
            <GradeBreakdown data={data.grades.length > 0 ? data.grades : [{grade: "—", count: 0}]} isDark={isDark} />
          </EmptyOverlay>
        </div>

        {/* Row 3: Manager Team Size + Entities */}
        <div className="grid grid-cols-2 gap-4">
          <EmptyOverlay isEmpty={data.managers.length === 0} isDark={isDark}>
            <ManagerTeamSize data={data.managers.length > 0 ? data.managers : [{manager: "—", team_size: 0},{manager: "—", team_size: 0}]} isDark={isDark} />
          </EmptyOverlay>
          <EmptyOverlay isEmpty={data.operating_entities.length === 0} isDark={isDark}>
            <EntitiesTable data={data.operating_entities.length > 0 ? data.operating_entities : [{entity: "—", count: 0}]} />
          </EmptyOverlay>
        </div>
      </div>
    </RegionalLayout>
  );
}
